import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  AppBar,
  IconButton,
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Button,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Avatar,
  Menu,
  MenuItem,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/List';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';

interface NavigationProps {
  currentView: 'dashboard' | 'add-transaction' | 'transactions' | 'budgets' | 'accounts' | 'goals' | 'loan-calculator' | 'reports' | 'settings';
  onViewChange: (view: 'dashboard' | 'add-transaction' | 'transactions' | 'budgets' | 'accounts' | 'goals' | 'loan-calculator' | 'reports' | 'settings') => void;
}

const Navigation: React.FC<NavigationProps> = ({ currentView, onViewChange }) => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { currentUser, logout } = useAuth();
  const drawerWidth = 240;


  // Navigation items principales (visibles dans bottom nav mobile)
  const primaryItems = [
    { id: 'dashboard', label: 'Accueil', icon: <DashboardIcon /> },
    { id: 'transactions', label: 'Transactions', icon: <ListIcon /> },
    { id: 'budgets', label: 'Budgets', icon: <AccountBalanceWalletIcon /> },
    { id: 'goals', label: 'Objectifs', icon: <SavingsIcon /> },
  ];

  // Items secondaires (dans menu ou speed dial)
  const secondaryItems = [
    { id: 'add-transaction', label: 'Ajouter Transaction', icon: <AddIcon /> },
    { id: 'accounts', label: 'Comptes', icon: <AccountBalanceIcon /> },
    { id: 'loan-calculator', label: 'Calculateur', icon: <AccountBalanceIcon /> },
    { id: 'reports', label: 'Rapports', icon: <AssessmentIcon /> },
    { id: 'settings', label: 'Paramètres', icon: <SettingsIcon /> },
  ];

  const handleMenuClick = (view: any) => {
    onViewChange(view);
    setAnchorEl(null);
    setSpeedDialOpen(false);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    setAnchorEl(null);
  };

  const drawerContent = (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      height: '100%',
      color: 'white',
    }}>
      {/* Header moderne avec logo */}
      <Box sx={{ 
        p: 2, 
        textAlign: 'center',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(255, 255, 255, 0.05)',
        height: '140px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <img 
          src="/ezbudget-removebg-preview.png" 
          alt="EzBudget Logo" 
          style={{ height: '120px', width: 'auto' }}
        />
      </Box>
      
      {/* Statut utilisateur moderne */}
      <Box sx={{ p: 2 }}>
        {currentUser ? (
          <Box sx={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            p: 2,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar sx={{ 
                width: 32, 
                height: 32, 
                mr: 1,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              }}>
                <PersonIcon fontSize="small" />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold', 
                  color: 'white',
                  fontSize: '0.85rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {currentUser.email}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  ✅ Synchronisé
                </Typography>
              </Box>
            </Box>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={logout}
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                borderWidth: '1px',
                borderRadius: '8px',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                backgroundColor: 'transparent',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.6)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              🚪 Déconnexion
            </Button>
          </Box>
        ) : (
          <Box sx={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            p: 2,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            textAlign: 'center',
          }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
              Connectez-vous pour synchroniser vos données
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setAuthModalOpen(true)}
              sx={{ 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '0.85rem',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(16, 185, 129, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              🔐 Se connecter
            </Button>
          </Box>
        )}
      </Box>
      
      {/* Navigation principale */}
      <Box sx={{ px: 1 }}>
        <Typography variant="overline" sx={{ 
          px: 2, 
          color: 'white', 
          fontSize: '0.9rem',
          fontWeight: 'bold',
          letterSpacing: '1px',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
          mb: 1,
        }}>
          PRINCIPAL
        </Typography>
        <List sx={{ py: 1 }}>
          {primaryItems.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={currentView === item.id}
                onClick={() => handleMenuClick(item.id)}
                sx={{
                  mx: 1,
                  borderRadius: '12px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    transform: 'translateX(4px)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: 'inherit',
                  minWidth: '40px',
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: currentView === item.id ? 'bold' : 'medium',
                    fontSize: '0.9rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Navigation secondaire */}
      <Box sx={{ px: 1 }}>
        <Typography variant="overline" sx={{ 
          px: 2, 
          color: 'white', 
          fontSize: '0.9rem',
          fontWeight: 'bold',
          letterSpacing: '1px',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
          mb: 1,
        }}>
          OUTILS
        </Typography>
        <List sx={{ py: 1 }}>
          {secondaryItems.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={currentView === item.id}
                onClick={() => handleMenuClick(item.id)}
                sx={{
                  mx: 1,
                  borderRadius: '12px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    transform: 'translateX(4px)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  color: 'inherit',
                  minWidth: '40px',
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: currentView === item.id ? 'bold' : 'medium',
                    fontSize: '0.9rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  // Navigation mobile avec bottom navigation
  if (isMobile) {
    return (
      <>
        {/* AppBar mobile simple */}
        <AppBar
          position="fixed"
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src="/ezbudget-removebg-preview.png" 
                alt="EzBudget Logo" 
                style={{ height: '32px', width: 'auto' }}
              />
            </Box>
            
            {/* Bouton utilisateur */}
            <IconButton
              onClick={currentUser ? handleUserMenuOpen : () => setAuthModalOpen(true)}
              sx={{ color: 'white' }}
            >
              {currentUser ? (
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }}>
                  <PersonIcon />
                </Avatar>
              ) : (
                <LoginIcon />
              )}
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Bottom Navigation */}
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: theme.zIndex.drawer + 1,
            borderTop: '1px solid rgba(0,0,0,0.1)',
          }} 
          elevation={3}
        >
          <BottomNavigation
            value={currentView}
            onChange={(event, newValue) => handleMenuClick(newValue)}
            sx={{ height: 70 }}
          >
            {primaryItems.map((item) => (
              <BottomNavigationAction
                key={item.id}
                label={item.label}
                value={item.id}
                icon={item.icon}
                sx={{
                  minWidth: 'auto',
                  '&.Mui-selected': {
                    color: 'primary.main',
                  },
                }}
              />
            ))}
            <BottomNavigationAction
              label="Plus"
              value="more"
              icon={<MoreVertIcon />}
              onClick={() => setSpeedDialOpen(true)}
              sx={{ minWidth: 'auto' }}
            />
          </BottomNavigation>
        </Paper>

        {/* Speed Dial pour actions supplémentaires */}
        <SpeedDial
          ariaLabel="Actions supplémentaires"
          sx={{ 
            position: 'fixed', 
            bottom: 85, 
            right: 16,
            '& .MuiFab-primary': {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }
          }}
          icon={<SpeedDialIcon />}
          open={speedDialOpen}
          onClose={() => setSpeedDialOpen(false)}
          onOpen={() => setSpeedDialOpen(true)}
        >
          {secondaryItems.map((item) => (
            <SpeedDialAction
              key={item.id}
              icon={item.icon}
              tooltipTitle={item.label}
              onClick={() => handleMenuClick(item.id)}
            />
          ))}
        </SpeedDial>

        {/* Menu utilisateur */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleUserMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              border: '1px solid rgba(0,0,0,0.1)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              minWidth: 200,
            }
          }}
        >
          {currentUser ? (
            [
              <MenuItem 
                key="profile" 
                disabled
                sx={{
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  borderRadius: '8px',
                  margin: '4px 8px',
                  '&.Mui-disabled': {
                    opacity: 1,
                  }
                }}
              >
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {currentUser.email}
                </Typography>
              </MenuItem>,
              <MenuItem 
                key="logout" 
                onClick={handleLogout}
                sx={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: 'error.main',
                  borderRadius: '8px',
                  margin: '4px 8px',
                  fontWeight: 'bold',
                  fontSize: '0.95rem',
                  padding: '12px 16px',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                    transform: 'scale(1.02)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <LogoutIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                🚪 Déconnexion
              </MenuItem>
            ]
          ) : (
            <MenuItem 
              onClick={() => setAuthModalOpen(true)}
              sx={{
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                color: 'success.main',
                borderRadius: '8px',
                margin: '4px 8px',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                padding: '12px 16px',
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.2)',
                  transform: 'scale(1.02)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <LoginIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
              🔐 Se connecter
            </MenuItem>
          )}
        </Menu>

        {/* Modal d'authentification */}
        <AuthModal
          open={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
        />
      </>
    );
  }

  // Navigation tablette - drawer compact
  if (isTablet) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Drawer
          variant="permanent"
          sx={{
            width: 80,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 80,
              boxSizing: 'border-box',
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <img 
              src="/ezbudget-removebg-preview.png" 
              alt="EzBudget Logo" 
              style={{ height: '40px', width: 'auto' }}
            />
          </Box>
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          <List>
            {[...primaryItems, ...secondaryItems].map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={currentView === item.id}
                  onClick={() => handleMenuClick(item.id)}
                  sx={{
                    flexDirection: 'column',
                    py: 2,
                    color: 'rgba(255, 255, 255, 0.8)',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(102, 126, 234, 0.2)',
                      color: 'white',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: 'inherit', 
                    minWidth: 'auto',
                    justifyContent: 'center',
                    mb: 0.5,
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    {item.label}
                  </Typography>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ mt: 'auto', p: 1, textAlign: 'center' }}>
            <IconButton
              onClick={currentUser ? handleLogout : () => setAuthModalOpen(true)}
              sx={{ 
                color: currentUser ? 'error.main' : 'success.main',
                backgroundColor: currentUser ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                border: currentUser ? '2px solid rgba(239, 68, 68, 0.3)' : '2px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px',
                padding: '8px',
                margin: '4px',
                transition: 'all 0.3s ease',
                '&:hover': { 
                  color: 'white',
                  backgroundColor: currentUser ? 'error.main' : 'success.main',
                  transform: 'scale(1.1)',
                  boxShadow: currentUser ? '0 4px 12px rgba(239, 68, 68, 0.4)' : '0 4px 12px rgba(16, 185, 129, 0.4)',
                }
              }}
            >
              {currentUser ? <LogoutIcon fontSize="small" /> : <LoginIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Drawer>
        
        <AuthModal
          open={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
        />
      </Box>
    );
  }

  // Navigation desktop - drawer complet (existant)
  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />
    </Box>
  );
};

export default Navigation;